import type { SupabaseClient } from "@supabase/supabase-js";

import type { ConsoleFilterRefusal } from "./console-filter-contract";

// Incidents data layer (CONSOLE-INCIDENTS-READ-SURFACE, migration 20260611183107). Reads
// come ONLY from the hosted management views - never field_incidents / evidence_attachments
// base tables. The views are the safe allowlist: no note bodies, client event ids, raw user
// ids, Storage keys, hashes, coordinates, device ids, tokens, or signed URLs. Guards get
// zero rows server-side. storage_attached is view-driven (after PRD-001AB S5: bytes confirmed,
// not merely storage_key present). The only media path is an explicit, user-triggered call
// to the JWT-verified field-evidence-signer Edge Function, which reuses
// authorize_field_evidence_read (and refuses unconfirmed attachments once S5 lands).

export const INCIDENT_LIST_VIEW = "management_field_incident_list";
export const ATTACHMENT_LIST_VIEW = "management_field_evidence_attachment_list";
export const FIELD_EVIDENCE_SIGNER_FUNCTION = "field-evidence-signer";
export const EVIDENCE_MEDIA_READ_TTL_SECONDS = 300;
export const INCIDENT_LIST_LIMIT = 200;
export const INCIDENT_LIST_COLUMNS =
  "incident_id, incident_status, category, severity, title, site_name, route_name, checkpoint_name, reported_by_label, reviewed_by_label, occurred_at, submitted_at, reviewed_at, locked_at, notes_present, attachment_count, photo_count, latest_attachment_at";
export const ATTACHMENT_LIST_COLUMNS =
  "attachment_id, kind, display_name, content_type, size_bytes, source, captured_at, storage_attached, sha256_present";

/** Closed host status values on management_field_incident_list.incident_status. */
export const INCIDENT_STATUS_VALUES = [
  "draft",
  "submitted",
  "under_review",
  "locked",
] as const;
export type IncidentStatusValue = (typeof INCIDENT_STATUS_VALUES)[number];

/** Closed severity values projected by the management view. */
export const INCIDENT_SEVERITY_VALUES = ["low", "medium", "high", "critical"] as const;
export type IncidentSeverityValue = (typeof INCIDENT_SEVERITY_VALUES)[number];

/**
 * Derived review predicates applied on the host (FC-09).
 * locked / under_review / awaiting_review mirror reviewStateLabel precedence.
 */
export const INCIDENT_REVIEW_STATE_VALUES = [
  "awaiting_review",
  "under_review",
  "locked",
] as const;
export type IncidentReviewStateValue = (typeof INCIDENT_REVIEW_STATE_VALUES)[number];

/**
 * Filters applied on the host BEFORE the 200-row window (D-10 / FC-08).
 * Date bounds use occurred_at; null occurred_at rows drop out of a bounded range
 * (SQL NULL fails gte/lte), and stay eligible when no date filter is set.
 */
export interface IncidentListFilters {
  readonly statuses?: readonly IncidentStatusValue[];
  readonly severities?: readonly IncidentSeverityValue[];
  readonly siteNames?: readonly string[];
  readonly reviewStates?: readonly IncidentReviewStateValue[];
  readonly fromIso?: string;
  readonly toIso?: string;
}

export type IncidentListRead =
  | { readonly status: "ok"; readonly rows: IncidentRow[]; readonly capped: boolean }
  | { readonly status: "empty"; readonly reason: "no_match" | "nothing_yet" }
  | { readonly status: "unavailable"; readonly reason: ConsoleFilterRefusal };

const LIST_LIMIT = INCIDENT_LIST_LIMIT;

export interface IncidentRow {
  incidentId: string;
  status: string;
  category: string | null;
  severity: string | null;
  title: string;
  siteName: string | null;
  routeName: string | null;
  checkpointName: string | null;
  reportedByLabel: string | null;
  reviewedByLabel: string | null;
  occurredAt: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  lockedAt: string | null;
  notesPresent: boolean;
  attachmentCount: number;
  photoCount: number;
  latestAttachmentAt: string | null;
}

export interface IncidentAttachment {
  attachmentId: string;
  kind: string | null;
  displayName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  source: string | null;
  capturedAt: string | null;
  storageAttached: boolean;
  sha256Present: boolean;
}

export interface IncidentEvidenceReadinessSummary {
  totalIncidents: number;
  totalAttachments: number;
  totalPhotos: number;
  incidentsWithAttachments: number;
  underReviewCount: number;
  lockedCount: number;
}

export interface IncidentEvidenceMediaReadUrl {
  signedUrl: string;
  contentType: string | null;
  providerExpiresInSeconds: number;
}

interface FieldEvidenceSignerReadResponse {
  accountId?: unknown;
  attachmentId?: unknown;
  contentType?: unknown;
  kind?: unknown;
  method?: unknown;
  operation?: unknown;
  providerExpiresInSeconds?: unknown;
  signedUrl?: unknown;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeId(value: string): string {
  return value.trim().toLowerCase();
}

function normalizedStatus(status: string): string {
  return status.trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_");
}

function isLockedIncident(incident: IncidentRow): boolean {
  return incident.lockedAt !== null || normalizedStatus(incident.status) === "locked";
}

function isUnderReviewIncident(incident: IncidentRow): boolean {
  if (isLockedIncident(incident)) return false;
  const status = normalizedStatus(incident.status);
  return (
    incident.reviewedAt !== null ||
    incident.reviewedByLabel !== null ||
    status === "under_review" ||
    status === "reviewed"
  );
}

export function reviewStateLabel(incident: IncidentRow): "Awaiting review" | "Under review" | "Locked" {
  if (isLockedIncident(incident)) return "Locked";
  if (isUnderReviewIncident(incident)) return "Under review";
  return "Awaiting review";
}

// --- Review / lock write path (PRD-001AB S7, migration 20260610010906) ------------------
//
// The console never UPDATEs field_incidents. Both transitions go through the audited
// SECURITY DEFINER RPCs, which re-check admin/supervisor membership and the legal state
// transition server-side and write an audit_events row. Everything below is UX gating on
// top of that: it decides which buttons to render, never whether the write is allowed.
//
// public.field_incident_status is ('draft', 'submitted', 'under_review', 'locked'). There is
// no separate "reviewed" state - review_field_incident accepts only 'under_review' and
// 'locked', so the two operator moves are submitted -> under_review and
// submitted|under_review -> locked. Both are one-way; there is no unlock RPC.

export const INCIDENT_REVIEW_RPC = "review_field_incident";
export const INCIDENT_LOCK_RPC = "lock_field_incident";
export const INCIDENT_STATUS_UNDER_REVIEW = "under_review";

export type IncidentWriteOutcome = { ok: true } | { ok: false; reason: string };

/**
 * The words both doors use for the same facts. Review and lock live on the incident feed and
 * on the detail desk, and QA found the two already stating different consequences for the
 * same irreversible action within one slice. The guards were shared from the start; these are
 * the sentences, so the wording cannot drift the way the behavior could not.
 */
export const INCIDENT_LOCK_FINALITY =
  "Locking is final. It closes the incident to new evidence and uploads, and there is no unlock action here. The change is recorded against your account.";

/** Evidence booleans, phrased once. "bytes confirmed" is the precise PRD-001AB S5 claim. */
export const INCIDENT_EVIDENCE_BYTES_CONFIRMED = "bytes confirmed";
export const INCIDENT_EVIDENCE_BYTES_UNCONFIRMED = "unconfirmed";
export const INCIDENT_EVIDENCE_CHECKSUM_PRESENT = "checksum recorded";
export const INCIDENT_EVIDENCE_CHECKSUM_ABSENT = "no checksum";

export const SAFE_INCIDENT_WRITE_FAILURE = "Couldn't update that incident - please try again.";

// Contract-stable, value-free sentences raised by the hosted review/lock RPCs. Anything
// else (including the interpolated 'Unsupported field incident review status: %') collapses
// to the safe copy so no backend detail, SQL text, or identifier reaches the operator.
const KNOWN_INCIDENT_WRITE_FAILURES: ReadonlySet<string> = new Set([
  "Authentication required to review field incidents.",
  "Authentication required to lock field incidents.",
  "Account id is required to review field incidents.",
  "Incident id is required to review field incidents.",
  "Only account admins and supervisors can review field incidents.",
  "Field incident was not found for this account.",
  "Only submitted field incidents can move under review.",
  "Only submitted or under-review field incidents can be locked.",
]);

/** submitted -> under_review is the only path the hosted RPC accepts for review. */
export function canStartIncidentReview(incident: IncidentRow): boolean {
  if (isLockedIncident(incident)) return false;
  return normalizedStatus(incident.status) === "submitted";
}

/** Locking accepts submitted or under_review, and is terminal. */
export function canLockIncident(incident: IncidentRow): boolean {
  if (isLockedIncident(incident)) return false;
  const status = normalizedStatus(incident.status);
  return status === "submitted" || status === INCIDENT_STATUS_UNDER_REVIEW;
}

async function runIncidentWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<IncidentWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    if (error === null) return { ok: true };
    const message = typeof error.message === "string" ? error.message : "";
    return {
      ok: false,
      reason: KNOWN_INCIDENT_WRITE_FAILURES.has(message) ? message : SAFE_INCIDENT_WRITE_FAILURE,
    };
  } catch {
    return { ok: false, reason: SAFE_INCIDENT_WRITE_FAILURE };
  }
}

/** Move a submitted incident under review. Server-side gated to admin/supervisor. */
export async function startIncidentReview(
  client: SupabaseClient,
  accountId: string,
  incidentId: string,
): Promise<IncidentWriteOutcome> {
  return runIncidentWrite(client, INCIDENT_REVIEW_RPC, {
    p_account_id: accountId,
    p_incident_id: incidentId,
    p_status: INCIDENT_STATUS_UNDER_REVIEW,
  });
}

/** Lock an incident. Terminal: blocks new evidence metadata and storage uploads. */
export async function lockIncident(
  client: SupabaseClient,
  accountId: string,
  incidentId: string,
): Promise<IncidentWriteOutcome> {
  return runIncidentWrite(client, INCIDENT_LOCK_RPC, {
    p_account_id: accountId,
    p_incident_id: incidentId,
  });
}

export function summarizeIncidentEvidenceReadiness(
  incidents: IncidentRow[],
): IncidentEvidenceReadinessSummary {
  return incidents.reduce<IncidentEvidenceReadinessSummary>(
    (summary, incident) => ({
      totalIncidents: summary.totalIncidents + 1,
      totalAttachments: summary.totalAttachments + incident.attachmentCount,
      totalPhotos: summary.totalPhotos + incident.photoCount,
      incidentsWithAttachments: summary.incidentsWithAttachments + (incident.attachmentCount > 0 ? 1 : 0),
      underReviewCount: summary.underReviewCount + (isUnderReviewIncident(incident) ? 1 : 0),
      lockedCount: summary.lockedCount + (isLockedIncident(incident) ? 1 : 0),
    }),
    {
      totalIncidents: 0,
      totalAttachments: 0,
      totalPhotos: 0,
      incidentsWithAttachments: 0,
      underReviewCount: 0,
      lockedCount: 0,
    },
  );
}

export function incidentListFiltersActive(filters: IncidentListFilters): boolean {
  return (
    (filters.statuses?.length ?? 0) > 0 ||
    (filters.severities?.length ?? 0) > 0 ||
    (filters.siteNames?.length ?? 0) > 0 ||
    (filters.reviewStates?.length ?? 0) > 0 ||
    typeof filters.fromIso === "string" ||
    typeof filters.toIso === "string"
  );
}

function isIncidentStatusValue(value: string): value is IncidentStatusValue {
  return (INCIDENT_STATUS_VALUES as readonly string[]).includes(value);
}

function isIncidentSeverityValue(value: string): value is IncidentSeverityValue {
  return (INCIDENT_SEVERITY_VALUES as readonly string[]).includes(value);
}

function sanitizeSiteNames(names: readonly string[] | undefined): string[] {
  if (names === undefined) return [];
  const out: string[] = [];
  for (const name of names) {
    const trimmed = name.trim();
    if (trimmed.length === 0) continue;
    // Keep labels the view already projects; reject control/bidi noise for query safety.
    if (/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/.test(trimmed)) continue;
    out.push(trimmed);
  }
  return [...new Set(out)].sort((left, right) => left.localeCompare(right));
}

/** PostgREST or() clauses for derived review-state predicates (FC-09). */
function reviewStateOrClause(states: readonly IncidentReviewStateValue[]): string | null {
  if (states.length === 0) return null;
  const parts: string[] = [];
  for (const state of states) {
    if (state === "locked") {
      parts.push("locked_at.not.is.null");
      parts.push("incident_status.eq.locked");
      continue;
    }
    if (state === "under_review") {
      // Not locked, and any of the under-review facts.
      parts.push(
        "and(locked_at.is.null,incident_status.neq.locked,reviewed_at.not.is.null)",
      );
      parts.push(
        "and(locked_at.is.null,incident_status.neq.locked,reviewed_by_label.not.is.null)",
      );
      parts.push("and(locked_at.is.null,incident_status.eq.under_review)");
      parts.push("and(locked_at.is.null,incident_status.eq.reviewed)");
      continue;
    }
    // awaiting_review: no lock facts and no review facts.
    parts.push(
      "and(locked_at.is.null,incident_status.neq.locked,reviewed_at.is.null,reviewed_by_label.is.null,incident_status.not.in.(under_review,reviewed))",
    );
  }
  return parts.length > 0 ? parts.join(",") : null;
}

function mapIncidentRow(raw: Record<string, unknown>): IncidentRow | null {
  const incidentId = asString(raw.incident_id);
  if (incidentId === null) return null;
  return {
    incidentId,
    status: asString(raw.incident_status) ?? "unknown",
    category: asString(raw.category),
    severity: asString(raw.severity),
    title: asString(raw.title) ?? "Incident",
    siteName: asString(raw.site_name),
    routeName: asString(raw.route_name),
    checkpointName: asString(raw.checkpoint_name),
    reportedByLabel: asString(raw.reported_by_label),
    reviewedByLabel: asString(raw.reviewed_by_label),
    occurredAt: asString(raw.occurred_at),
    submittedAt: asString(raw.submitted_at),
    reviewedAt: asString(raw.reviewed_at),
    lockedAt: asString(raw.locked_at),
    notesPresent: raw.notes_present === true,
    attachmentCount: asCount(raw.attachment_count),
    photoCount: asCount(raw.photo_count),
    latestAttachmentAt: asString(raw.latest_attachment_at),
  };
}

/**
 * Incident list rows, newest submission first.
 * Filters run on the host before the 200-row window. Malformed rows fail the whole read
 * as unavailable rather than disappearing into a shortened success list.
 */
export async function fetchIncidentList(
  client: SupabaseClient,
  accountId: string,
  filters: IncidentListFilters = {},
): Promise<IncidentListRead> {
  const statuses = (filters.statuses ?? []).filter(isIncidentStatusValue);
  const severities = (filters.severities ?? []).filter(isIncidentSeverityValue);
  const siteNames = sanitizeSiteNames(filters.siteNames);
  const reviewStates = [...new Set(filters.reviewStates ?? [])];
  const fromIso =
    typeof filters.fromIso === "string" && filters.fromIso.trim().length > 0
      ? filters.fromIso.trim()
      : null;
  const toIso =
    typeof filters.toIso === "string" && filters.toIso.trim().length > 0
      ? filters.toIso.trim()
      : null;

  let query = client
    .from(INCIDENT_LIST_VIEW)
    .select(INCIDENT_LIST_COLUMNS)
    .eq("account_id", accountId);

  if (statuses.length === 1) {
    query = query.eq("incident_status", statuses[0]!);
  } else if (statuses.length > 1) {
    query = query.in("incident_status", statuses);
  }
  if (severities.length === 1) {
    query = query.eq("severity", severities[0]!);
  } else if (severities.length > 1) {
    query = query.in("severity", severities);
  }
  if (siteNames.length === 1) {
    query = query.eq("site_name", siteNames[0]!);
  } else if (siteNames.length > 1) {
    query = query.in("site_name", siteNames);
  }
  if (fromIso !== null) {
    query = query.gte("occurred_at", fromIso);
  }
  if (toIso !== null) {
    query = query.lte("occurred_at", toIso);
  }
  const reviewOr = reviewStateOrClause(reviewStates);
  if (reviewOr !== null) {
    query = query.or(reviewOr);
  }

  const { data, error } = await query
    .order("submitted_at", { ascending: false })
    .limit(LIST_LIMIT);

  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load incidents.");
  }

  const incidents: IncidentRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const mapped = mapIncidentRow(raw);
    if (mapped === null) {
      return { status: "unavailable", reason: "unavailable" };
    }
    incidents.push(mapped);
  }

  if (incidents.length === 0) {
    const activeFilters: IncidentListFilters = {
      statuses,
      severities,
      siteNames,
      reviewStates,
    };
    if (fromIso !== null) {
      Object.assign(activeFilters, { fromIso });
    }
    if (toIso !== null) {
      Object.assign(activeFilters, { toIso });
    }
    return {
      status: "empty",
      reason: incidentListFiltersActive(activeFilters) ? "no_match" : "nothing_yet",
    };
  }

  return {
    status: "ok",
    rows: incidents,
    capped: incidents.length >= LIST_LIMIT,
  };
}

/** Attachment METADATA for one incident (no bytes/URLs - those stay behind the authorize RPC). */
export async function fetchIncidentAttachments(
  client: SupabaseClient,
  accountId: string,
  incidentId: string,
): Promise<IncidentAttachment[]> {
  const { data, error } = await client
    .from(ATTACHMENT_LIST_VIEW)
    .select(ATTACHMENT_LIST_COLUMNS)
    .eq("account_id", accountId)
    .eq("incident_id", incidentId)
    .order("captured_at", { ascending: true })
    .limit(LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load incident evidence.");
  }
  const attachments: IncidentAttachment[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const attachmentId = asString(raw.attachment_id);
    if (attachmentId === null) continue;
    attachments.push({
      attachmentId,
      kind: asString(raw.kind),
      displayName: asString(raw.display_name),
      contentType: asString(raw.content_type),
      sizeBytes: typeof raw.size_bytes === "number" ? raw.size_bytes : null,
      source: asString(raw.source),
      capturedAt: asString(raw.captured_at),
      storageAttached: raw.storage_attached === true,
      sha256Present: raw.sha256_present === true,
    });
  }
  return attachments;
}

/**
 * Requests a short-lived signed read URL for an attachment the management view marks as
 * storage_attached (bytes confirmed after S5). The console never signs Storage paths
 * directly; this calls the JWT-verified Edge Function, which authorizes through
 * public.authorize_field_evidence_read and signs with the server-side Storage client.
 */
export async function requestIncidentEvidenceMediaReadUrl(
  client: SupabaseClient,
  accountId: string,
  attachmentId: string,
  expiresInSeconds = EVIDENCE_MEDIA_READ_TTL_SECONDS,
): Promise<IncidentEvidenceMediaReadUrl> {
  const { data, error } = await client.functions.invoke<FieldEvidenceSignerReadResponse>(
    FIELD_EVIDENCE_SIGNER_FUNCTION,
    {
      body: {
        accountId,
        attachmentId,
        expiresInSeconds,
        operation: "read",
      },
    },
  );
  if (error !== null || !isRecord(data)) {
    throw new Error("Could not open evidence media.");
  }

  const signedUrl = typeof data.signedUrl === "string" ? data.signedUrl : "";
  const contentType = typeof data.contentType === "string" ? data.contentType : null;
  const providerExpiresInSeconds =
    typeof data.providerExpiresInSeconds === "number" && Number.isFinite(data.providerExpiresInSeconds)
      ? data.providerExpiresInSeconds
      : 0;

  if (
    data.kind !== "field-evidence-signed-url" ||
    data.operation !== "read" ||
    data.method !== "GET" ||
    !isHttpsUrl(signedUrl) ||
    normalizeId(String(data.accountId ?? "")) !== normalizeId(accountId) ||
    normalizeId(String(data.attachmentId ?? "")) !== normalizeId(attachmentId) ||
    providerExpiresInSeconds < 1 ||
    providerExpiresInSeconds > 3600
  ) {
    throw new Error("Could not open evidence media.");
  }

  return {
    contentType,
    providerExpiresInSeconds,
    signedUrl,
  };
}
